import React, { useState, useEffect, useCallback } from 'react';
import { Chip, Box, Autocomplete, IconButton } from '@mui/material';
import { Check as CheckIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { WcnTextField } from '@/components';
import { equalsIgnoreCase } from '@core/utils';
import { useEmailInput } from '@core/hooks/useEmailInput.ts';

export interface EmailInputRef {
    hasUnsavedEmail: () => boolean;
}

interface EmailInputProps {
    recipientEmails: string[];
    initialEmails?: string[];
    onUpdate: (emails: string[]) => void;
    label?: string;
    variant?: 'outlined' | 'standard' | 'filled';
    onRefReady?: (ref: EmailInputRef) => void;
    onError?: (email: string) => string;
    onWarning?: (email: string) => string;
    disabledEmails?: string[];
    onChipWarning?: (email: string) => string;
}

const CheckboxIcon = ({ checked }: { checked: boolean }) => (
    <span className={`multi-select-checkbox ${checked ? 'checked' : ''}`}>
        {checked && <CheckIcon className="multi-select-checkbox-check" />}
    </span>
);

function EmailInput({
    recipientEmails = [],
    initialEmails = [],
    onUpdate,
    label,
    variant = 'standard',
    onRefReady,
    onError,
    onWarning,
    disabledEmails = [],
    onChipWarning
}: EmailInputProps) {
    const { t } = useTranslation();
    const [emailList, setEmailList] = useState<string[]>(initialEmails);

    useEffect(() => setEmailList(initialEmails), [initialEmails]);

    const handleRemove = useCallback(
        (email: string) => {
            const updated = emailList.filter((e) => !equalsIgnoreCase(e, email));
            setEmailList(updated);
            onUpdate(updated);
        },
        [emailList, onUpdate]
    );

    const handleCommit = useCallback(
        (newValue: string[]) => {
            setEmailList(newValue);
            onUpdate(newValue);
        },
        [onUpdate]
    );

    const {
        inputValue,
        error,
        warning,
        isOptionDisabled,
        handleKeyDownCapture,
        handleChange,
        handleInputChange,
        clearInput
    } = useEmailInput({
        emailList,
        recipients: recipientEmails,
        disabledEmails,
        onError,
        onWarning,
        onRemove: handleRemove,
        onCommit: handleCommit,
        onRefReady
    });

    const hasError = !!error;

    return (
        <Box>
            <Autocomplete
                multiple
                freeSolo
                disableClearable
                disableCloseOnSelect
                filterSelectedOptions={false}
                options={recipientEmails}
                value={emailList}
                inputValue={inputValue}
                getOptionDisabled={(option) => isOptionDisabled(option as string)}
                getOptionLabel={(option: any) => {
                    if (!option) return '';
                    return typeof option === 'string' ? option : option.label || '';
                }}
                onKeyDownCapture={handleKeyDownCapture}
                onInputChange={handleInputChange}
                onChange={handleChange}
                renderTags={() => null}
                renderOption={(props, option) => {
                    const { key, className: muiClassName, onClick, ...restProps } = props as any;
                    const isDisabled = isOptionDisabled(option as string);
                    const isSelected = emailList.some((e) => e.toLowerCase() === (option as string).toLowerCase());
                    return (
                        <Box
                            key={key}
                            component="li"
                            {...restProps}
                            onClick={isDisabled ? undefined : onClick}
                            className={`selector-option multi-select-option${isSelected ? ' selected' : ''}${isDisabled ? ' disabled' : ''}`}
                        >
                            <CheckboxIcon checked={isSelected} />
                            <span>{option as string}</span>
                        </Box>
                    );
                }}
                renderInput={(params) => (
                    <WcnTextField
                        {...params}
                        label={label}
                        variant={variant}
                        fullWidth
                        error={hasError}
                        helperText={t(error)}
                        warning={!hasError && !!warning}
                        warningText={t(warning)}
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: inputValue ? (
                                <IconButton
                                    size="small"
                                    sx={{
                                        padding: '2px',
                                        color: 'var(--gray-medium)',
                                        '& i': { fontSize: '14px', lineHeight: 1 }
                                    }}
                                    onMouseDown={(e) => {
                                        if (e.button !== 0) return;
                                        e.preventDefault();
                                        clearInput();
                                    }}
                                >
                                    <i className="bi bi-x-lg" />
                                </IconButton>
                            ) : (
                                params.InputProps.endAdornment
                            )
                        }}
                    />
                )}
            />
            <Box className="email-chips">
                {emailList.map((email, index) => {
                    const chipWarning = onChipWarning?.(email);
                    return (
                        <Box key={`${email}-${index}`} className="email-chip-cell">
                            <Chip
                                label={email}
                                onDelete={() => handleRemove(email)}
                                className={`email-chip${chipWarning ? ' email-chip--warning' : ''}`}
                                sx={{ '& .MuiChip-label': { paddingTop: '1px' } }}
                            />
                            {!!chipWarning && <Box className="email-chip-warning-text">{t(chipWarning)}</Box>}
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}

export default EmailInput;
